import React from 'react';
import { Link } from 'react-router-dom';
import {
  StarIcon,
  MapPinIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { Influencer } from '../../types';

interface InfluencerCardProps {
  influencer: Influencer;
}

export const InfluencerCard: React.FC<InfluencerCardProps> = ({ influencer }) => {
  const getTotalFollowers = () => {
    return Object.values(influencer.followerCounts).reduce((total, count) => +total + (+count || 0), 0);
  };

  const formatFollowerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <StarIcon key="half" className="h-4 w-4 text-yellow-400 fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarIcon key={`empty-${i}`} className="h-4 w-4 text-gray-300" />
      );
    }

    return stars;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Profile Header */}
      <div className="p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative">
            <img
              src={influencer.profileImage || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(influencer.name || 'User') + '&background=6366f1&color=fff&size=64'}
              alt={influencer.name}
              className="w-16 h-16 rounded-full object-cover"
            />
            {influencer.isVerified && (
              <CheckBadgeIcon className="absolute -top-1 -right-1 h-5 w-5 text-blue-500" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-bold text-gray-900">{influencer.name}</h3>
              {influencer.isVerified && (
                <CheckBadgeIcon className="h-5 w-5 text-blue-500" />
              )}
            </div>
            <p className="text-gray-600 text-sm">{influencer.mainCategory}</p>
            <div className="flex items-center space-x-2 mt-1">
              <MapPinIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500">{influencer.location}</span>
            </div>
          </div>
        </div>

        {/* Bio */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{influencer.bio}</p>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {formatFollowerCount(getTotalFollowers())} followers
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {formatCurrency(influencer.ratePerPost)}/post
            </span>
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center space-x-2 mb-4">
          <div className="flex items-center">
            {renderStars(influencer.rating)}
          </div>
          <span className="text-sm text-gray-600">
            {influencer.rating.toFixed(1)} rating
          </span>
          <span className="text-sm text-gray-500">
            • {influencer.engagementRate}% engagement
          </span>
        </div>

        {/* Social Media Platforms */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Platforms</h4>
          <div className="flex flex-wrap gap-2">
            {influencer.followerCounts.instagram && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-pink-100 text-pink-800">
                Instagram: {formatFollowerCount(influencer.followerCounts.instagram)}
              </span>
            )}
            {influencer.followerCounts.youtube && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                YouTube: {formatFollowerCount(influencer.followerCounts.youtube)}
              </span>
            )}
            {influencer.followerCounts.tiktok && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                TikTok: {formatFollowerCount(influencer.followerCounts.tiktok)}
              </span>
            )}
            {influencer.followerCounts.twitter && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Twitter: {formatFollowerCount(influencer.followerCounts.twitter)}
              </span>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
          <div className="flex flex-wrap gap-2">
            {influencer.categories.map((category, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800"
              >
                {category}
              </span>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Languages</h4>
          <div className="flex flex-wrap gap-2">
            {influencer.languages.map((language, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
              >
                {language}
              </span>
            ))}
          </div>
        </div>

        {/* Availability */}
        {influencer.availableFrom && influencer.availableTo && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Available</h4>
            <p className="text-sm text-gray-600">
              {new Date(influencer.availableFrom).toLocaleDateString()} -
              {new Date(influencer.availableTo).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <Link
            to={`/influencers/${influencer.influencerId}`}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View Profile
          </Link>
          <Link
            to={`/influencers/${influencer.influencerId}/contact`}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            Contact
          </Link>
        </div>
      </div>
    </div>
  );
};
